import { Button } from '@odekoteam/doppio';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Flex, Heading, Image, Paragraph } from 'theme-ui';

import FileUploadIcon from '@/assets/images/FileUpload.svg';
import Modal from '@/components/Modal/modal';
import { parseCsv } from '@/services/BulkUpload/utils/file';

export default function ImportItemListModal({
  visible,
  onClose,
  onConfirm,
}: {
  visible: boolean;
  onClose: () => void;
  onConfirm: (itemIds: string[]) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uniqueItemIds, setUniqueItemIds] = useState<string[]>([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: 5000000,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    onDropRejected: (errors) => {
      setUploadErrors(
        errors.flatMap((e) => e.errors.map((err) => err.message)),
      );
      setFile(null);
      setUniqueItemIds([]);
    },
    onDrop: async (acceptedFiles) => {
      setUploadErrors([]);
      if (acceptedFiles.length > 0) {
        const droppedFile = acceptedFiles[0];
        setFile(droppedFile);
        try {
          const result = await parseCsv<{ 'Item Uuid': string }>(droppedFile);
          const ids = [
            ...new Set(
              result.data
                .map((row) => row['Item Uuid']?.trim())
                .filter(Boolean),
            ),
          ];
          setUniqueItemIds(ids);
        } catch {
          setUniqueItemIds([]);
        }
      }
    },
  });

  const handleClose = () => {
    setFile(null);
    setUniqueItemIds([]);
    setUploadErrors([]);
    onClose();
  };

  const handleConfirm = () => {
    if (uniqueItemIds.length > 0) {
      onConfirm(uniqueItemIds);
      handleClose();
    }
  };

  return (
    <Modal visible={visible} contentStyle={{ maxWidth: '520px' }}>
      <Heading variant="headingSm" sx={{ mb: 3 }}>
        Import Item List
      </Heading>
      <Flex
        {...getRootProps()}
        sx={{
          gap: 2,
          minHeight: 120,
          borderRadius: 2,
          cursor: 'pointer',
          border: '2px dashed',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          borderColor: isDragActive ? 'primary' : 'secondaryDisabled',
          backgroundColor: isDragActive ? 'backgroundWeak' : 'transparent',
          '&:hover': { borderColor: 'primary', backgroundColor: '#F9F9F7' },
        }}
      >
        <input {...getInputProps()} />
        <Image src={FileUploadIcon} sx={{ width: '18px', height: '18px' }} />
        <div style={{ textAlign: 'center' }}>
          <div>
            <span style={{ fontFamily: 'Roobert-Bold' }}>Click to upload</span>{' '}
            or drag and drop
          </div>
          <span style={{ fontSize: 10 }}>CSV (4MB max)</span>
        </div>
      </Flex>
      {file ? (
        <Flex
          sx={{
            p: 3,
            mt: 3,
            borderRadius: 2,
            border: '1px solid',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderColor: 'secondaryDisabled',
          }}
        >
          <Paragraph sx={{ fontSize: 1, fontWeight: 'bold' }}>
            {file.name}
          </Paragraph>
          <Paragraph sx={{ fontSize: 0, color: 'neutralMedium' }}>
            {(file.size / 1024).toFixed(1)} KB
          </Paragraph>
        </Flex>
      ) : null}
      {file && uniqueItemIds.length > 0 ? (
        <Paragraph sx={{ mt: 2, fontSize: 1, textAlign: 'center' }}>
          {uniqueItemIds.length} unique item
          {uniqueItemIds.length !== 1 ? 's' : ''}
        </Paragraph>
      ) : null}
      {uploadErrors.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          {uploadErrors.map((msg, i) => (
            <Paragraph key={i} sx={{ fontSize: 1, color: 'errorDefault' }}>
              {msg}
            </Paragraph>
          ))}
        </Box>
      ) : null}
      <Flex sx={{ mt: 4, gap: 3, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onPress={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={handleConfirm}
          isDisabled={uniqueItemIds.length === 0}
        >
          Confirm
        </Button>
      </Flex>
    </Modal>
  );
}
